// Точка входа парсера: разбор вставленной ссылки/текста в черновики рейсов.
// Чистый модуль (владелец: agent-parser): НЕ импортирует src/core и src/store.

import type { Currency } from '../types';
import { lookupTz, airportCity, KNOWN_TIMEZONES } from './iata';
import { CARRIERS } from './carriers';
import { parseText, type TextLeg } from './text';
import { parseUrl, findUrls, type UrlLeg } from './url';

export { lookupTz, airportCity, CARRIERS, KNOWN_TIMEZONES };

export interface ParsedFlightDraft {
  from: string;
  to: string;
  fromTz: string;
  toTz: string;
  departLocal: string;
  arriveLocal: string;
  carrier: string;
  flightNumber?: string;
  priceMinor: number | null;
  currency: Currency;
  /** какие поля НЕ распознаны — UI подсвечивает их */
  missing: string[];
  /** человекочитаемые заметки о том, как разобрано */
  notes: string[];
}

export interface ParseResult {
  ok: boolean;
  drafts: ParsedFlightDraft[];
  source: 'url' | 'text' | 'mixed';
  errors: string[];
}

function isEmptyStr(v: string): boolean {
  return v === '';
}

/** Выбирает значение из текста с приоритетом, иначе — из ссылки; добавляет note об источнике. */
function pickField(textVal: string, urlVal: string, label: string, notes: string[]): string {
  if (!isEmptyStr(textVal)) {
    if (!isEmptyStr(urlVal) && urlVal !== textVal) {
      notes.push(`Поле «${label}» взято из текста (в ссылке было другое значение — приоритет у текста).`);
    }
    return textVal;
  }
  if (!isEmptyStr(urlVal)) {
    notes.push(`Поле «${label}» получено из ссылки (в тексте не найдено).`);
    return urlVal;
  }
  return '';
}

interface MergedLeg {
  from: string;
  to: string;
  departDate: string;
  departTime: string;
  arriveDate: string;
  arriveTime: string;
  carrier: string;
  flightNumber: string;
  priceMinor: number | null;
  currency: Currency;
  notes: string[];
}

function emptyTextLeg(): TextLeg {
  return {
    from: '',
    to: '',
    departDate: '',
    departTime: '',
    arriveDate: '',
    arriveTime: '',
    carrier: '',
    flightNumber: '',
    priceMinor: null,
    currency: 'USD',
    notes: [],
  };
}

function emptyUrlLeg(): UrlLeg {
  return { from: '', to: '', departDate: '', notes: [] };
}

function mergeLeg(textLeg: TextLeg | undefined, urlLeg: UrlLeg | undefined): MergedLeg {
  const t = textLeg ?? emptyTextLeg();
  const u = urlLeg ?? emptyUrlLeg();
  const notes: string[] = [...t.notes, ...u.notes];

  const from = pickField(t.from, u.from, 'from', notes);
  const to = pickField(t.to, u.to, 'to', notes);
  const departDate = pickField(t.departDate, u.departDate, 'дата вылета', notes);
  // UrlLeg не содержит даты прилёта (ссылка не даёт этой информации) — берём только из текста.
  const arriveDate = t.arriveDate;

  return {
    from,
    to,
    departDate,
    departTime: t.departTime,
    arriveDate,
    arriveTime: t.arriveTime,
    carrier: t.carrier,
    flightNumber: t.flightNumber,
    priceMinor: t.priceMinor,
    currency: t.currency,
    notes,
  };
}

function buildDraft(leg: MergedLeg): ParsedFlightDraft {
  const missing: string[] = [];
  const notes: string[] = [...leg.notes];

  if (!leg.from) missing.push('from');
  if (!leg.to) missing.push('to');

  const fromTz = leg.from ? lookupTz(leg.from) : '';
  const toTz = leg.to ? lookupTz(leg.to) : '';
  if (!fromTz) {
    missing.push('fromTz');
    if (leg.from) notes.push(`Таймзона аэропорта ${leg.from} не найдена в справочнике — уточните вручную.`);
  }
  if (!toTz) {
    missing.push('toTz');
    if (leg.to) notes.push(`Таймзона аэропорта ${leg.to} не найдена в справочнике — уточните вручную.`);
  }

  let departLocal = '';
  if (leg.departDate && leg.departTime) {
    departLocal = `${leg.departDate}T${leg.departTime}`;
  } else if (leg.departDate) {
    departLocal = `${leg.departDate}T00:00`;
    missing.push('departLocal', 'departTime');
    notes.push('Время вылета не указано в источнике — используется 00:00, требуется уточнение.');
  } else {
    missing.push('departLocal');
  }

  let arriveLocal = '';
  if (leg.arriveDate && leg.arriveTime) {
    arriveLocal = `${leg.arriveDate}T${leg.arriveTime}`;
  } else if (leg.arriveDate) {
    arriveLocal = `${leg.arriveDate}T00:00`;
    missing.push('arriveLocal', 'arriveTime');
    notes.push('Время прилёта не указано в источнике — используется 00:00, требуется уточнение.');
  } else {
    missing.push('arriveLocal');
  }

  if (!leg.carrier) missing.push('carrier');
  if (leg.priceMinor === null) missing.push('price');

  return {
    from: leg.from,
    to: leg.to,
    fromTz,
    toTz,
    departLocal,
    arriveLocal,
    carrier: leg.carrier,
    flightNumber: leg.flightNumber || undefined,
    priceMinor: leg.priceMinor,
    currency: leg.currency,
    missing,
    notes,
  };
}

/**
 * Разбирает вставленную ссылку и/или текст выдачи в черновики рейсов.
 * Никаких сетевых запросов — разбирается только переданная строка.
 */
export function parseInput(raw: string, opts?: { year?: number }): ParseResult {
  const errors: string[] = [];
  const input = raw ?? '';

  if (!input.trim()) {
    return {
      ok: false,
      drafts: [buildDraft(mergeLeg(undefined, undefined))],
      source: 'text',
      errors: ['Пустой ввод — вставьте ссылку или текст с деталями рейса.'],
    };
  }

  const urls = findUrls(input);
  let textOnly = input;
  for (const u of urls) {
    textOnly = textOnly.split(u).join(' ');
  }

  const urlLegs: UrlLeg[] = [];
  const urlNotes: string[] = [];
  for (const u of urls) {
    const res = parseUrl(u, opts);
    urlLegs.push(...res.legs);
    urlNotes.push(...res.notes);
  }

  const textResult = parseText(textOnly, opts);
  const textLegs = textResult.legs;

  const hasUrl = urlLegs.length > 0;
  const hasText = textLegs.length > 0;
  const source: ParseResult['source'] = hasUrl && hasText ? 'mixed' : hasUrl ? 'url' : 'text';

  const legCount = Math.max(textLegs.length, urlLegs.length, 1);
  const drafts: ParsedFlightDraft[] = [];
  for (let i = 0; i < legCount; i++) {
    const merged = mergeLeg(textLegs[i], urlLegs[i]);
    if (i === 0) {
      merged.notes.push(...textResult.notes, ...urlNotes);
    }
    drafts.push(buildDraft(merged));
  }

  const ok = drafts.some((d) => d.from !== '' || d.to !== '' || d.departLocal !== '');
  if (!ok) {
    errors.push('Не удалось распознать ни аэропортов, ни даты/времени вылета — заполните поля вручную.');
  }

  return { ok, drafts, source, errors };
}
